import { useState } from "react";

import type { Meta, StoryObj } from "@storybook/react";

import CalendarToggleCard from "@/ui/components/calendarSync/CalendarToggleCard";
import type { CalendarToggleCardProps } from "@/ui/components/calendarSync/CalendarToggleCard";
import { Box } from "@/ui/layout";

const StoryMeta: Meta<CalendarToggleCardProps> = {
  title: "Components/Calendar Sync/Toggle Card",
  component: CalendarToggleCard,
  argTypes: {
    isDeviceCalendarEnabled: { control: "boolean" },
    isGoogleCalendarEnabled: { control: "boolean" },
    onDeviceCalendarChange: { action: "device calendar toggled" },
    onGoogleCalendarChange: { action: "google calendar toggled" },
    shouldShowDeviceCalendar: { control: "boolean" },
    testID: { control: "text" },
    title: { control: "text" },
  },
  args: {
    isDeviceCalendarEnabled: false,
    isGoogleCalendarEnabled: false,
    testID: "storybook-calendar-toggle",
    title: "Sync calendars",
  },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render({
    isDeviceCalendarEnabled: isInitialDeviceEnabled,
    isGoogleCalendarEnabled: isInitialGoogleEnabled,
    onDeviceCalendarChange,
    onGoogleCalendarChange,
    shouldShowDeviceCalendar,
    testID,
    title,
  }) {
    const [isDeviceCalendarEnabled, setIsDeviceCalendarEnabled] = useState(
      isInitialDeviceEnabled,
    );
    const [isGoogleCalendarEnabled, setIsGoogleCalendarEnabled] = useState(
      isInitialGoogleEnabled,
    );

    return (
      <Box width="100%">
        <CalendarToggleCard
          isDeviceCalendarEnabled={isDeviceCalendarEnabled}
          isGoogleCalendarEnabled={isGoogleCalendarEnabled}
          onDeviceCalendarChange={(isEnabled) => {
            setIsDeviceCalendarEnabled(isEnabled);
            onDeviceCalendarChange(isEnabled);
          }}
          onGoogleCalendarChange={(isEnabled) => {
            setIsGoogleCalendarEnabled(isEnabled);
            onGoogleCalendarChange(isEnabled);
          }}
          shouldShowDeviceCalendar={shouldShowDeviceCalendar}
          testID={testID}
          title={title}
        />
      </Box>
    );
  },
};

type Story = StoryObj<CalendarToggleCardProps>;

const Primary: Story = {};

const BothEnabled: Story = {
  args: {
    isDeviceCalendarEnabled: true,
    isGoogleCalendarEnabled: true,
  },
};

const GoogleOnly: Story = {
  args: {
    isDeviceCalendarEnabled: false,
    isGoogleCalendarEnabled: true,
  },
};

const DeviceOnly: Story = {
  args: {
    isDeviceCalendarEnabled: true,
    isGoogleCalendarEnabled: false,
  },
};

const ProfileTitle: Story = {
  args: {
    title: "Connected calendars",
  },
};

const IOSPlatform: Story = {
  args: {
    isDeviceCalendarEnabled: true,
    isGoogleCalendarEnabled: true,
    shouldShowDeviceCalendar: true,
  },
  name: "Platform: iOS",
  parameters: {
    docs: {
      description: {
        story:
          "On iOS devices, both Google Calendar and Apple calendar toggles are shown.",
      },
    },
  },
};

const AndroidPlatform: Story = {
  args: {
    isGoogleCalendarEnabled: true,
    shouldShowDeviceCalendar: false,
  },
  name: "Platform: Android",
  parameters: {
    docs: {
      description: {
        story:
          "On Android devices, only Google Calendar toggle is shown. The Apple calendar row is hidden.",
      },
    },
  },
};

export {
  AndroidPlatform,
  BothEnabled,
  DeviceOnly,
  GoogleOnly,
  IOSPlatform,
  Primary,
  ProfileTitle,
};
export default StoryMeta;
